import { Button } from '@/components/ui/button';
import {
  MessageSquareText,
  Sparkles,
  CheckCircle2,
  ListChecks,
  ArrowRight,
  Smartphone,
  Check,
  Clock3,
  Zap,
} from 'lucide-react';
import { DEPOSIT_BANKS } from '@/supabase/deposit/banks';
import { cn } from '@/lib/utils';

interface DepositsIntroProps {
  accountStatus: 'unlinked' | 'in_progress';
  onStartOnboarding: () => void;
}

// 색 언어는 입금 큐(DepositRow)와 동일 문법: blue=자동/확실, emerald=입금 완료, amber=확인 대기(수동).
// 인트로가 제품의 색 문법을 미리 학습시키는 구조 — 새 액센트 도입 금지.
const STEPS = [
  {
    icon: MessageSquareText,
    chip: 'bg-blue-100 text-blue-600',
    label: 'text-blue-600',
    title: '입금 문자 수신',
    description: '연동한 계좌로 입금 문자가 오면 셀러헬퍼가 자동으로 읽어요.',
  },
  {
    icon: Zap,
    chip: 'bg-emerald-100 text-emerald-600',
    label: 'text-emerald-600',
    title: '정산서에서 자동 매칭',
    description: '입금자명과 금액으로 주문을 찾아 정산서 화면에서 먼저 자동 매칭돼요.',
  },
  {
    icon: ListChecks,
    chip: 'bg-amber-100 text-amber-600',
    label: 'text-amber-600',
    title: '남은 입금만 여기서',
    description: "확인이 필요한 입금만 이 화면에 모여요. 주문을 확인해 확정하면 '입금 완료'로 바뀌어요.",
  },
];

// 은행별 브랜드 컬러 도트 (Tailwind 기본 팔레트 근사). 미지정 코드는 중립 도트로 폴백.
const BANK_DOT: Record<string, string> = {
  Woori: 'bg-blue-500',
  KB: 'bg-yellow-400',
  Shinhan: 'bg-sky-500',
  Hana: 'bg-teal-500',
  Nonghyup: 'bg-green-500',
  IBK: 'bg-sky-700',
};

const SUPPORTED_BANKS = DEPOSIT_BANKS.filter((bank) => bank.onboardingAvailable);

// 매칭 키(입금자명·금액) 형광펜 — 문자 카드와 주문 카드에 동일 스타일로 넣어
// "입금자명 + 금액으로 찾는다"는 매칭 원리를 문장 없이 시각으로 전달.
function MatchKey({ children }: { children: React.ReactNode }) {
  return <span className="rounded bg-blue-100 px-1 font-bold text-blue-800">{children}</span>;
}

// 모의 매칭 데모: 페이지 로드 시 1회 재생(문자 도착 → 자동 매칭 → '매칭 완료' 플립).
// 무한 루프 아님 — 산만함 방지. prefers-reduced-motion이면 완료 상태 정지 화면.
const DEMO_STYLES = `
@keyframes di-drop { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }
@keyframes di-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes di-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes di-fade-out { from { opacity: 1; } to { opacity: 0; } }
@keyframes di-dash-move { to { background-position: 0 12px; } }
.di-sms { animation: di-drop 0.5s ease-out both; }
.di-chip { animation: di-rise 0.45s ease-out 0.7s both; }
.di-order { animation: di-rise 0.5s ease-out 1.1s both; }
.di-badge-before { animation: di-fade-out 0.35s ease-out 2s both; }
.di-badge-after { animation: di-fade-in 0.35s ease-out 2s both; }
.di-dash {
  background-image: repeating-linear-gradient(to bottom, rgb(147 197 253) 0 6px, transparent 6px 12px);
  /* 데모 오케스트레이션(~2.5s)과 함께 끝나는 유한 반복 — '1회 재생' 약속 준수 */
  animation: di-dash-move 0.9s linear 3;
}
@media (prefers-reduced-motion: reduce) {
  .di-sms, .di-chip, .di-order, .di-badge-after, .di-dash { animation: none; }
  .di-badge-before { animation: none; opacity: 0; }
}
`;

// 미연동 셀러용 랜딩형 인트로 — /deposits 첫 방문 전환율 목적 (spec §4)
export function DepositsIntro({ accountStatus, onStartOnboarding }: DepositsIntroProps) {
  return (
    <div className="relative overflow-hidden rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200 md:p-10">
      <style>{DEMO_STYLES}</style>

      {/* 배경 글로우: blue(자동)·emerald(완료) — 희망찬 톤, 콘텐츠 대비는 침범하지 않는 저채도 블러 */}
      <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-100/60 blur-3xl" />

      <div className="relative grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        {/* ── 좌: 카피 + CTA ───────────────────────────── */}
        <div className="text-left">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-bold tracking-wide text-white">
              NEW
            </span>
            <span className="text-[13px] font-bold text-blue-700">
              정산서 <span aria-hidden="true">↔</span> 입금 자동매칭
            </span>
          </div>

          {/* 사실관계: 매칭은 자동, '입금 완료' 확정은 확인 1클릭 — 자동확정처럼 과약속하지 않는다 */}
          <h2 className="mt-3 break-keep text-2xl font-bold leading-snug text-slate-900 md:text-[28px]">
            입금 문자가 오면
            <br />
            주문을 알아서 찾고,
            <br />
            확인 한 번이면 <span className="text-emerald-600">'입금 완료'</span>
          </h2>

          <p className="mt-3 break-keep text-[15px] leading-relaxed text-slate-600">
            입금 문자가 올 때마다 셀러헬퍼가 입금자명과 금액으로
            정산서 주문을 찾아 자동으로 매칭합니다.
          </p>

          {/* 핵심 베네핏 = 하이라이트 박스 (출시 팝업과 동일 패턴, emerald 승격) */}
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-emerald-50 px-3.5 py-3 ring-1 ring-inset ring-emerald-200/70">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <p className="break-keep text-sm font-medium leading-relaxed text-emerald-900">
              방송 끝나고 하던 입금 확인 —
              <br />
              더 이상 하나하나 수기로 대조하지 않아도 됩니다.
            </p>
          </div>

          {accountStatus === 'in_progress' && (
            <p className="mt-4 inline-flex max-w-full items-center gap-1.5 break-keep rounded-full bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 ring-1 ring-amber-200">
              <Clock3 className="h-4 w-4 shrink-0" aria-hidden />
              진행하던 연동이 있어요 — 이어서 금방 끝낼 수 있어요.
            </p>
          )}

          <Button
            className="mt-6 min-h-12 w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-7 text-base font-bold text-white shadow-lg shadow-blue-600/25 transition-colors hover:from-blue-700 hover:to-indigo-700 sm:w-auto"
            onClick={onStartOnboarding}
          >
            {accountStatus === 'in_progress' ? '계좌 연동 계속하기' : '1분 만에 계좌 연동하기'}
            <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
          </Button>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1">
              <Clock3 className="h-4 w-4 text-slate-400" aria-hidden />
              평균 1분 소요
            </span>
          </div>

          <div className="mt-5 space-y-2">
            <p className="text-xs font-semibold text-slate-500">지원 은행</p>
            <div className="flex flex-wrap gap-1.5">
              {SUPPORTED_BANKS.map((bank) => (
                <span
                  key={bank.code}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-[13px] font-medium text-slate-700 ring-1 ring-slate-200"
                >
                  <span className={cn('h-2 w-2 rounded-full', BANK_DOT[bank.code] ?? 'bg-slate-300')} aria-hidden />
                  {bank.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── 우: 모의 매칭 데모 (시그니처) — 데이터는 하드코딩 데모, 실제 데이터와 무관 ── */}
        <div className="rounded-2xl bg-gradient-to-b from-slate-50 to-blue-50/60 p-5 ring-1 ring-slate-200/80">
          <p className="text-xs font-semibold tracking-wide text-slate-500">
            미리보기 — 입금 문자 한 통이 이렇게 처리돼요
          </p>

          {/* 1. 은행 입금 문자 (실제 SMS 말풍선 재현, 은행 마스킹 포함) */}
          <div className="di-sms mt-3 rounded-2xl rounded-tl-sm bg-white p-3.5 text-left shadow-sm ring-1 ring-slate-200">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
              <Smartphone className="h-3.5 w-3.5" aria-hidden />
              입금 문자
              <span className="ml-auto tabular-nums">오후 2:03</span>
            </div>
            <p className="mt-1.5 break-keep text-sm font-medium leading-relaxed text-slate-800">
              [Web발신] 입금 <MatchKey>50,000원</MatchKey>
              <br />
              <MatchKey>김*희</MatchKey>
            </p>
          </div>

          {/* 2. 자동 매칭 커넥터 (점선 흐름 + blue 칩) */}
          <div className="relative flex justify-center py-1">
            <span aria-hidden className="di-dash block h-14 w-0.5" />
            <span className="di-chip absolute top-1/2 inline-flex -translate-y-1/2 items-center gap-1 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-bold text-white shadow-md shadow-blue-600/30">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              셀러헬퍼 자동 매칭
            </span>
          </div>

          {/* 3. 정산서 주문 행 (실제 DepositRow의 좌측 레일 문법 축소 재현) */}
          <div className="di-order relative overflow-hidden rounded-xl bg-white text-left shadow-sm ring-1 ring-slate-200">
            <span className="absolute inset-y-0 left-0 w-[3px] bg-emerald-500" aria-hidden />
            <div className="flex items-center gap-3 py-3 pl-4 pr-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900">
                  <MatchKey>김민희</MatchKey>님 주문
                </p>
                <p className="mt-0.5 text-[13px] tabular-nums text-slate-500">
                  복숭아 2박스 · <MatchKey>50,000원</MatchKey>
                </p>
              </div>
              {/* 배지 플립: amber '입금 전' → emerald '매칭 완료' (확정은 확인 1클릭 — '입금 완료'로 쓰면 자동확정 과약속) */}
              <div className="relative shrink-0">
                <span className="di-badge-after inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  매칭 완료
                </span>
                <span
                  aria-hidden
                  className="di-badge-before absolute inset-0 inline-flex items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700"
                >
                  입금 전
                </span>
              </div>
            </div>
          </div>

          {/* 마지막 단계의 정직한 마무리 — 확정은 셀러의 확인 한 번 */}
          <p className="mt-2.5 break-keep text-center text-[13px] font-medium text-slate-500">
            확인 버튼 한 번이면 <span className="font-bold text-emerald-700">'입금 완료'</span>로 확정돼요
          </p>
        </div>
      </div>

      {/* ── 하단: 3단계 흐름 (실제 순서 = 넘버링 정당, 단계별 의미색) ── */}
      <div className="relative mt-8 border-t border-slate-100 pt-7">
        <div className="grid gap-4 sm:grid-cols-3 sm:gap-8">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={step.title} className="relative rounded-xl bg-slate-50/80 p-4 text-left ring-1 ring-slate-100">
                <div className="flex items-center gap-2.5">
                  <span className={cn('flex h-9 w-9 items-center justify-center rounded-xl', step.chip)}>
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <span className={cn('text-xs font-bold tracking-wide', step.label)}>STEP {index + 1}</span>
                </div>
                <p className="mt-2.5 break-keep text-[15px] font-semibold text-slate-900">{step.title}</p>
                {/* 중장년 셀러 가독성: 14px + slate-600 대비 + break-keep 유지 */}
                <p className="mt-1 break-keep text-sm leading-relaxed text-slate-600">{step.description}</p>
                {index < STEPS.length - 1 && (
                  <ArrowRight
                    aria-hidden
                    className="absolute -right-[26px] top-1/2 hidden h-5 w-5 -translate-y-1/2 text-slate-300 sm:block"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
